import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { FC } from 'react';

export type DncSubmitDialogState = 'confirm' | 'loading' | 'success';

export interface VerifyConversationLink {
  label: string;
  href: string;
}

interface DncSubmitDialogProps {
  open: boolean;
  state: DncSubmitDialogState;
  phone: string | null;
  scope: 'campaign' | 'company';
  companyName?: string;
  reportsHref: string | null;
  conversationLinks: VerifyConversationLink[];
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
}

const CONVERSATION_LINK_SCROLL_THRESHOLD = 5;

export const DncSubmitDialog: FC<DncSubmitDialogProps> = ({
  open,
  state,
  phone,
  scope,
  companyName,
  reportsHref,
  conversationLinks,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const title =
    state === 'confirm' ? 'Confirm DNC' : state === 'loading' ? 'Adding to DNC…' : 'DNC added';

  // Closing the dialog is only allowed in confirm/success states — during
  // loading, the mutation is in flight and we don't want to leave the modal
  // half-closed while the request is still resolving.
  const handleClose = () => {
    if (state === 'confirm') onCancel();
    else if (state === 'success') onClose();
  };

  const hasConversationLinks = conversationLinks.length > 0;
  const shouldScrollLinks = conversationLinks.length > CONVERSATION_LINK_SCROLL_THRESHOLD;

  const organizationLabel =
    scope === 'campaign' ? companyName ?? 'this organization' : 'your organization';

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="dnc-submit-dialog-title" fullWidth>
      <DialogTitle id="dnc-submit-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {state === 'confirm' && (
          <DialogContentText component="div">
            This will add <strong>{phone}</strong> to the DNC list. The lead will no longer receive
            any messages from {organizationLabel} in any of your campaigns. This action cannot be
            undone from the Client UI. Continue?
          </DialogContentText>
        )}

        {state === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={48} />
          </Box>
        )}

        {state === 'success' && (
          <Stack spacing={2}>
            <DialogContentText component="div">
              You can verify <strong>{phone}</strong> was DNC&apos;d in the following places:
            </DialogContentText>

            {reportsHref && (
              <Link
                href={reportsHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#1976d2' }}
              >
                Reports → Do Not Contact list
                <OpenInNewIcon fontSize="inherit" />
              </Link>
            )}

            {hasConversationLinks ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  {scope === 'company' && conversationLinks.length > 1
                    ? 'Conversation pages (one per campaign where this lead exists):'
                    : 'Conversation page:'}
                </Typography>
                <Box
                  sx={shouldScrollLinks ? { maxHeight: 260, overflowY: 'auto', pr: 1 } : undefined}
                >
                  <Stack spacing={0.5}>
                    {conversationLinks.map((link) => (
                      <Link
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: '#1976d2',
                        }}
                      >
                        {link.label}
                        <OpenInNewIcon fontSize="inherit" />
                      </Link>
                    ))}
                  </Stack>
                </Box>
              </>
            ) : (
              scope === 'company' && (
                <Typography variant="body2" color="text.secondary">
                  This phone isn&apos;t in any of your campaigns yet. Future leads with this number
                  will be blocked automatically.
                </Typography>
              )
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {state === 'confirm' && (
          <>
            <Button onClick={onCancel} autoFocus>
              Cancel
            </Button>
            <Button onClick={onConfirm} variant="contained">
              Add to DNC
            </Button>
          </>
        )}
        {state === 'success' && (
          <Button onClick={onClose} variant="contained" autoFocus>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
